import type { BigStepInstance } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { SubStepStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, HStack, Text, VStack } from '@enechain/ecloud-designsystem'
import { FaCheckSolid } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import {
  bigStepDisplayStatusOf,
  isBigStepComplete,
  isBigStepCompletedAutomatically,
  isBigStepUnlocked,
} from '~/pages/WorkflowCaseDetail/models/workflowCaseDetail'

type Props = {
  bigSteps: BigStepInstance[]
}

/**
 * ウィザード型ステッパー (design/demand/case-detail.html `.wizard-steps.case-detail` /
 * `.wizard-step` / `.wizard-step-connector` 相当)。
 *
 * 大ステップを丸数字 + タイトルで横並び表示する。 タイトルの下に、 その大ステップが
 * 「自動完了」 か 「手動完了」 かを小さく出す (2026-10-07 デザイナー指摘)。 判定は
 * `isBigStepCompletedAutomatically` (全サブステップがリンク先画面の操作で自動完了 =
 * AC-WFW-007f の時だけ自動完了)。 8080 はこの行に大ステップの説明を出しているが、
 * 8080 は AC-WFW-007f (リンク先画面の操作での自動完了) を持たないので、 実装だけの表示。
 */
export const WizardStepper = ({ bigSteps }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const sortedSteps = bigSteps
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)

  return (
    <HStack
      align="center"
      justify="center"
      flexWrap="wrap"
      gap={4}
      rowGap={2}
      padding="12px 16px"
      background="Surface.primary"
    >
      {sortedSteps.map((bs, idx) => {
        // 状態判定は BigStepAccordion と同じ共通ヘルパーに寄せる。 以前は
        // 「done = 全 substep が raw isCompleted」「active = done でなく 1 件以上
        // 完了」という独自判定で、 同じステップが上下の UI で違う状態に見えていた
        // (2026-08-17 デザイナー指摘)。 ズレは 2 つあった:
        //   進行中でも完了 substep が 0 件だと active にならず、 未着手と同じ
        //   灰色で描かれていた。 共通ヘルパーは「未完了かつロックされていない
        //   = current」で判定する。
        const prevComplete =
          idx === 0 ? true : isBigStepComplete(sortedSteps[idx - 1].subSteps)
        const isLocked = !isBigStepUnlocked(idx, prevComplete)
        const doneCount = bs.subSteps.filter(
          (ss) => ss.status === SubStepStatus.COMPLETE,
        ).length
        const status = bigStepDisplayStatusOf(
          doneCount,
          bs.subSteps.length,
          isLocked,
        )
        const done = status === 'complete'
        const active = status === 'current'
        const isLast = idx === sortedSteps.length - 1

        return (
          <HStack key={bs.displayOrder} align="center" gap={4}>
            <HStack align="center" gap={2}>
              <Box
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                display="inline-flex"
                boxSize="40px"
                color={done ? 'Text.highEmphasisInverse' : 'Text.midEmphasis'}
                background={done ? 'Surface.accentPrimary' : 'Surface.primary'}
                borderWidth="2px"
                borderColor={
                  done || active ? 'Border.accentPrimary' : 'Border.midEmphasis'
                }
                borderRadius="full"
              >
                {done ? (
                  <FaCheckSolid boxSize={iconSize.stepperMark} />
                ) : (
                  <Text as="span" textStyle="sectionTitle">
                    {bs.displayOrder}
                  </Text>
                )}
              </Box>
              <VStack align="flex-start" gap={0}>
                <Text textStyle="sectionTitle" color="Text.highEmphasis">
                  {localizeBuiltinWorkflowText(bs.title, t)}
                </Text>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {isBigStepCompletedAutomatically(bs.subSteps)
                    ? t('page:workflowCaseDetail.stepper.autoComplete')
                    : t('page:workflowCaseDetail.stepper.manualComplete')}
                </Text>
              </VStack>
            </HStack>
            {!isLast && (
              <Box
                flexShrink={0}
                width="48px"
                height="2px"
                background={
                  done ? 'Surface.accentPrimary' : 'Border.lowEmphasis'
                }
              />
            )}
          </HStack>
        )
      })}
    </HStack>
  )
}
